<template>
  <div class="container">
    <PostSkeleton v-if="!fanId || contentStore.isContentFetching" />
    <Post
      v-else-if="
        !contentStore.isContentFetching && contentStore.content && contentStore.content.length > 0
      "
      ref="postRef"
      :post="contentStore.content[0]"
      :clickable="false"
      :fullscreen_allowed="true"
    />
    <ErrorMessage
      v-else-if="
        !contentStore.isContentFetching &&
        (!contentStore.content || contentStore.content.length === 0)
      "
      :title="t('errors.contentErrorTitle')"
      :subtitle="t('errors.contentErrorSubtitle')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useContentStore } from '@stores/content.store';
import { useFanStore } from '@stores/fan.store';
import { useI18n } from 'vue-i18n';
import { useTelemetryFetcher } from '@api/tracking.api';
import { useScreenOrientation } from '@vueuse/core';
import { isMobile } from '@basitcodeenv/vue3-device-detect';
import { useVideoPlayerStore } from '@stores/videoPlayer.store';

import PostSkeleton from '@modules/Post/PostSkeleton.vue';
import Post from '@modules/Post/Post.vue';
import ErrorMessage from '@modules/ErrorMessage.vue';

const route = useRoute();
const contentStore = useContentStore();
const fanStore = useFanStore();

const { fanId } = storeToRefs(fanStore);
const { contentId } = storeToRefs(contentStore);
const { t } = useI18n();

const videoPlayerStore = useVideoPlayerStore(); // shared orientation state?
const { isSupported: isOrientationSupported, orientation } = useScreenOrientation();
const isDeviceOrientationLandscape = computed(() => orientation.value === 'landscape-primary');
const postRef = ref<InstanceType<typeof Post> | null>(null);
watch(isDeviceOrientationLandscape, (value) => {
  if (!isMobile && !isOrientationSupported) return;

  if (postRef.value) postRef.value.toggleFullscreen();

  // needed at all?
  if (value) videoPlayerStore.enableFullscreenOnRotate();
  else videoPlayerStore.disableFullscreenOnRotate();
});

watch(
  fanId,
  (newFanId) => {
    if (newFanId) contentId.value = (route.params.id as string) || '';
  },
  { immediate: true },
);

watch(
  contentId,
  async () => {
    if (contentId.value) {
      try {
        await contentStore.fetchContent(contentId.value);

        if (!contentStore.content || contentStore.content.length === 0) {
          return;
        }

        useTelemetryFetcher().execute({
          metric: 'view-post',
          resource: window.location.href,
        });
      } catch (error) {
        console.error(error);
      }
    } else contentStore.resetContent();
  },
  { immediate: true },
);
</script>
